<template>
  <div class="customily-pack-item-group">
    <!-- Pack item option selection: Same as Item 1 vs Customize a different design -->
    <div v-if="allowCopyPrevious" class="pack-copy-option-cards" :style="isSameAsPrevious ? {marginBottom: '12px'} : undefined">
      <label
        class="pack-copy-card"
        style="margin-bottom: 0;"
        :class="{ active: isSameAsPrevious }"
        @click="selectSameAsPrevious"
      >
        <div class="card-radio">
          <input
            type="radio"
            :name="`pack-choice-${packIdx}`"
            :checked="isSameAsPrevious"
          />
          <span class="radio-custom"></span>
        </div>
        <div class="card-content">
          <div class="card-title">Same as Item 1</div>
          <div class="card-subtitle">Use the same design and details as Item 1</div>
        </div>
      </label>

      <label
        class="pack-copy-card"
        style="margin-bottom: 0;"
        :class="{ active: !isSameAsPrevious }"
        @click="selectDifferentDesign"
      >
        <div class="card-radio">
          <input
            type="radio"
            :name="`pack-choice-${packIdx}`"
            :checked="!isSameAsPrevious"
          />
          <span class="radio-custom"></span>
        </div>
        <div class="card-content">
          <div class="card-title">Customize a different design</div>
          <div class="card-subtitle">Choose different name, color, etc.</div>
        </div>
      </label>
    </div>

    <!-- Options inputs (rendered when Item 1 or when "Customize a different design" is selected) -->
    <template v-if="!allowCopyPrevious || !isSameAsPrevious">
    <!-- Mỗi entry là một ô nhập, hoặc (isAction) khối nút/âm thanh voice. Khi CMS chọn "Voice fields",
         các ô đó gom cùng khối voice thành một thẻ riêng; thứ tự hai khối theo "Display order". -->
    <GroupWrapper v-for="group in layoutGroups" :key="group.key" :card="group.card">
    <template v-for="{ key, opt, index, isAction } in group.items" :key="key">
    <div
      v-if="!isAction"
      :opt-id="index + 1"
      :sort-id="index + 1"
      class="customily_option"
    >
      <!-- Case text-only: pack này không có MediaUploader nào, khách tự bấm generate.
           Chỉ đặt trước field "Any Change..." nếu field đó tồn tại; các case khác
           (không có field này, hoặc rỗng field) giữ nguyên vị trí cuối (xem fallback dưới v-for). -->
      <div
        v-if="isStyleTransferMode && !isVoice && mediaUploaderOptions.length === 0 && index === anyChangeFieldIndex"
        class="gen-only-text-action"
      >
        <button
          type="button"
          class="music-action-button"
          @click="handleGenImageFromOnlyText"
          :disabled="statusGenAIText === 'waiting'"
        >
          <span v-if="statusGenAIText === 'waiting'">⏳ Generating... {{ uploadProgress }}%</span>
          <span v-else>Create Your Design</span>
        </button>

        <span
          v-if="personalizationValues['imageOutputUrl'] && statusGenAIText !== 'waiting'"
          class="customization-upload-image-wrapper-message text-primary"
        >
          Success! Enjoy Your Preview
        </span>

        <!-- History section -->
        <div v-if="isStyleTransferMode && filteredHistory.length">
          <div class="ai-style-transfer-history-title">History</div>
          <div class="ai-style-transfer-recently">
            <div
              v-for="(item, itemIndex) in filteredHistory"
              :key="itemIndex"
              class="ai-style-transfer-recently__item"
              style="z-index: 98;"
              :class="{
                'ai-style-transfer-recently__item--active':
                  item.image === personalizationValues['imageOutputUrl'],
              }"
              @click="chooseHistoryImage(itemIndex, 'imageOutputUrl')"
            >
              <div class="ai-style-transfer-recently__item--image">
                <img
                  :src="item.image"
                  class="ai-style-transfer-recently__media"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- For InputText -->
      <div
        v-if="opt.type === 'InputText'"
        class="cl-accordion cl-option-content"
      >
        <div class="mb-sm">
          <div class="customization-upload-image-upload-title">
            {{ opt.label }}
            <span v-if="opt.required" style="color: red">*</span>
          </div>
          <div class="sib-input-wrapper">
            <Input
              :key="inputResetKey"
              :modelValue="personalizationValues[opt.name] || ''"
              @update:modelValue="(val: string) => $emit('update:personalization', opt.name, val)"
              :placeholder="opt.placeholder || ''"
              :maxlength="opt.maxLength || undefined"
              class="sib-input sib-input--role-default sib-input--size-md sib-input--round-md sib-input--type-input"
            />
          </div>
          <div
            v-if="validationErrors && validationErrors[opt.name]"
            class="customization-upload-image-wrapper-message text-secondary"
            style="margin-top: 4px; display: block;"
          >
            {{ validationErrors[opt.name] }}
          </div>
          <small
            v-if="opt.helpText"
            class="help-text"
            style="white-space: pre-wrap; display: block;"
            >{{ opt.helpText }}</small
          >
        </div>
      </div>

      <!-- For Dropdown -->
      <div
        v-else-if="opt.type === 'Dropdown'"
        class="cl-accordion cl-option-content"
      >
        <div class="mb-sm">
          <div class="customization-upload-image-upload-title">
            {{ opt.label }}
            <span v-if="opt.required" style="color: red">*</span>
          </div>
          <div class="sib-input-wrapper">
            <select
              :multiple="opt.multiple"
              @change="(e) => onDropdownChange(e, opt)"
              class="sib-input sib-input--role-default sib-input--size-md sib-input--round-md"
              :name="`properties[${opt.name}]${opt.multiple ? '[]' : ''}`"
            >
              <option v-if="!opt.multiple" value="" disabled :selected="!personalizationValues[opt.name]">{{ opt.placeholder || 'Select an option' }}</option>
              <option
                v-for="choice in opt.options || []"
                :key="choice.value"
                :value="choice.value"
                :selected="isDropdownOptionSelected(opt, choice.value)"
              >
                {{ choice.label }}
              </option>
            </select>
          </div>
          <div
            v-if="validationErrors && validationErrors[opt.name]"
            class="customization-upload-image-wrapper-message text-secondary"
            style="margin-top: 4px; display: block;"
          >
            {{ validationErrors[opt.name] }}
          </div>
          <small
            v-if="opt.helpText"
            class="help-text"
            style="white-space: pre-wrap; display: block;"
            >{{ opt.helpText }}</small
          >
        </div>
      </div>

      <!-- For MediaUploader -->
      <div
        v-else-if="opt.type === 'MediaUploader'"
        class="cl-accordion cl-option-content"
      >
        <div class="customily-file-input wrapper-style-transfer">
          <div class="sib-position-custom-personalization">
            <div class="customization-upload-image-wrapper">
              <div class="customization-upload-image-upload-title">
                {{ opt.label }}
                <span v-if="opt.required" style="color: red">*</span>
              </div>

              <FileUpload
                :id="'leversphere-upload-' + opt.name + '-' + packIdx"
                text="Select Image"
                :loading="loadingAI"
                :accept="opt.mediaType?.join(',') || 'image/png,image/jpeg'"
                @change="(event: Event) => handleUploadImage(event, opt.name)"
                class="sib-file-select sib-file-select--role-default sib-file-select--size-md sib-file-select--round-none sib-button sib-button--default"
              />

              <span v-if="loadingAI" class="tooltip-image-processing"
                >Processing {{ uploadProgress }}%</span
              >

              <span
                v-if="personalizationValues[opt.name] && !loadingAI"
                class="remove-custom-upload-image"
                @click="$emit('remove-image', opt.name)"
              >
                X
              </span>

              <span
                v-if="personalizationValues[opt.name] && !loadingAI"
                class="customization-upload-image-wrapper-message text-primary"
              >
                {{ opt.tooltip || 'Success! Enjoy Your Preview' }}
              </span>

              <div
                v-if="!personalizationValues[opt.name] && ((validationErrors && validationErrors[opt.name]) || uploaderErrors[opt.name])"
                class="customization-upload-image-wrapper-message text-secondary mt-xs"
                style="display: block; margin-top: 4px;"
              >
                {{ (validationErrors && validationErrors[opt.name]) || uploaderErrors[opt.name] }}
              </div>

              <!-- History section -->
              <div v-if="isStyleTransferMode && filteredHistory.length">
                <div class="ai-style-transfer-history-title">History</div>
                <div class="ai-style-transfer-recently">
                  <div
                    v-for="(item, itemIndex) in filteredHistory"
                    :key="itemIndex"
                    class="ai-style-transfer-recently__item"
                    style="z-index: 98;"
                    :class="{
                      'ai-style-transfer-recently__item--active':
                        item.image === personalizationValues[opt.name] ||
                        item.imageUploadUrl === personalizationValues[opt.name],
                    }"
                    @click="chooseHistoryImage(itemIndex, opt.name)"
                  >
                    <div class="ai-style-transfer-recently__item--image">
                      <img
                        :src="item.image"
                        class="ai-style-transfer-recently__media"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              </div>

              <small
                v-if="opt.helpText"
                class="help-text"
                style="white-space: pre-wrap; display: block; margin-top: 10px; margin-bottom: 10px;"
                >{{ opt.helpText }}</small
              >
            </div>
          </div>
        </div>
      </div>

      <!-- For TextArea -->
      <div
        v-else-if="opt.type === 'TextArea'"
        class="cl-accordion cl-option-content"
      >
        <label
          class="customily_option_label"
          style="display: block; font-weight: bold; margin-bottom: 5px;"
        >
          {{ opt.label }}
          <span v-if="opt.required" style="color: red">*</span>
        </label>
        <textarea
          :required="opt.required"
          :value="personalizationValues[opt.name] || ''"
          @input="(e) => $emit('update:personalization', opt.name, (e.target as HTMLTextAreaElement).value)"
          :placeholder="
            opt.placeholder || 'E.g: Adjust hair, adjust body, change color,...'
          "
          :rows="opt.rows || 3"
          class="input-field-v2 textarea-field"
          :name="`properties[${opt.name}]`"
          style="width: 100%"
        ></textarea>
        <div
          v-if="validationErrors && validationErrors[opt.name]"
          class="customization-upload-image-wrapper-message text-secondary"
          style="margin-top: 4px;"
        >
          {{ validationErrors[opt.name] }}
        </div>
        <small
          v-if="opt.helpText"
          class="help-text"
          style="white-space: pre-wrap; display: block;"
          >{{ opt.helpText }}</small
        >
      </div>
    </div>

    <!-- Voice template (CMS output_type = voice): khách điền text ở các field phía trên và/hoặc upload voice
         (tuỳ voice_mode), bấm nút để tạo audio. Output lưu ở personalizationValues['voiceOutputUrl']. -->
    <div v-else class="gen-only-text-action">
      <div v-if="needsVoiceInput" class="mb-sm">
        <div class="customization-upload-image-upload-title">
          Upload your voice
          <span style="color: red">*</span>
        </div>
        <input
          type="file"
          accept=".mp3,.wav,.mpga,audio/mpeg,audio/wav"
          :disabled="voiceUploading || statusGenVoice === 'waiting'"
          @change="handleVoiceFileChange"
        />
        <small class="help-text" style="display: block">
          MP3 or WAV, a clear recording of 3 to 10 seconds (max 10 MB).
        </small>
        <audio
          v-if="personalizationValues['voiceInputUrl']"
          :src="personalizationValues['voiceInputUrl']"
          controls
          style="width: 100%; margin-top: 8px"
        ></audio>
      </div>
      <button
        type="button"
        class="music-action-button"
        :disabled="statusGenVoice === 'waiting' || voiceUploading"
        @click="handleGenVoice"
      >
        <span v-if="voiceUploading">⏳ Uploading...</span>
        <span v-else-if="statusGenVoice === 'waiting'">⏳ Generating...</span>
        <span v-else>Create Your Voice</span>
      </button>
      <div
        v-if="voiceError || (validationErrors && validationErrors['__voice'])"
        class="customization-upload-image-wrapper-message text-secondary"
        style="margin-top: 8px"
      >
        {{ voiceError || (validationErrors && validationErrors['__voice']) }}
      </div>
      <div
        v-if="personalizationValues['voiceOutputUrl'] && statusGenVoice !== 'waiting'"
        style="margin-top: 12px"
      >
        <span class="customization-upload-image-wrapper-message text-primary">
          Success! Listen to your voice
        </span>
        <audio
          :src="personalizationValues['voiceOutputUrl']"
          controls
          controlsList="nodownload"
          @contextmenu.prevent
          style="width: 100%; margin-top: 8px"
        ></audio>
      </div>
    </div>
    </template>
    </GroupWrapper>

    <!-- Fallback: case text-only nhưng KHÔNG có field "Any Change..." (không tìm thấy, hoặc
         optionsConfig rỗng) — giữ nguyên vị trí cũ, hiện sau cùng toàn bộ field. -->
    <div
      v-if="isStyleTransferMode && !isVoice && mediaUploaderOptions.length === 0 && anyChangeFieldIndex === -1"
      class="gen-only-text-action"
    >
      <button
        type="button"
        class="music-action-button"
        @click="handleGenImageFromOnlyText"
        :disabled="statusGenAIText === 'waiting'"
      >
        <span v-if="statusGenAIText === 'waiting'">⏳ Generating... {{ uploadProgress }}%</span>
        <span v-else>Create Your Design</span>
      </button>

      <span
        v-if="personalizationValues['imageOutputUrl'] && statusGenAIText !== 'waiting'"
        class="customization-upload-image-wrapper-message text-primary"
      >
        Success! Enjoy Your Preview
      </span>

      <!-- History section -->
      <div v-if="isStyleTransferMode && filteredHistory.length">
        <div class="ai-style-transfer-history-title">History</div>
        <div class="ai-style-transfer-recently">
          <div
            v-for="(item, itemIndex) in filteredHistory"
            :key="itemIndex"
            class="ai-style-transfer-recently__item"
            style="z-index: 98;"
            :class="{
              'ai-style-transfer-recently__item--active':
                item.image === personalizationValues['imageOutputUrl'],
            }"
            @click="chooseHistoryImage(itemIndex, 'imageOutputUrl')"
          >
            <div class="ai-style-transfer-recently__item--image">
              <img
                :src="item.image"
                class="ai-style-transfer-recently__media"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import Input from '@/ui-kits/Input.vue'
import FileUpload from '@/ui-kits/FileSelect.vue'
import { fabric } from 'fabric'
import useProductStore from '@/store/product'
import useCustomizationStore from '@/store/customization'
import CacheService, { getJSONfromCacheResponse } from '@/services/cache'
import { cloneDeep } from '@/utils'
import {
  SEVEN_DAYS_IN_MILLISECONDS,
  CUSTOMILY_CANVAS_WRAPPER_CLASS,
  CUSTOMILY_PREVIEW_CANVAS_ID,
  CUSTOMILY_CANVAS_CONTAINER_CLASS,
  PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ID,
  PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ACTIVE_CLASS,
  EVENT_PRODUCT_UPDATE_MAIN_SLIDER,
  EVENT_PRODUCT_SHOW_PREVIEW,
} from '@/utils'
import EventBus from '@/services/eventbus'
import Analytics from '@/services/analytics'
import Provider from '@/provider'
import { defineComponent, h } from 'vue'

interface TextLayerOptions {
  left: number
  top: number
  width?: number
  fontSize?: number
  fontFamily?: string
  fontWeight?: string
  fill?: string
  originX?: string
  originY?: string
  textAlign?: string
  angle?: number
  opacity?: number
}

interface ImageLayerOptions {
  left: number
  top: number
  width: number
  height: number
  angle?: number
  opacity?: number
}

const props = defineProps<{
  packIdx: number
  activePackIdx: number
  optionsConfig: any[]
  personalizationValues: Record<string, string>
  uploaderErrors: Record<string, string>
  validationErrors?: Record<string, string>
  isStyleTransferMode: boolean
  activeTemplateId?: string // templateIdSelected từ cha
  activeTemplate?: any      // activeTemplate config từ cha
  allowCopyPrevious?: boolean
  inputResetKey?: number // đổi giá trị để remount ô nhập (Input.vue không sync lại modelValue)
  entryId?: string // activeEntryForPrompt từ cha — entry Multi Variant/Multi Prompt đang chọn, BE dùng để lấy đúng prompt
  pillCustomilyOptions?: Record<string, string> // pill (Multi Prompt) đang chọn, gộp vào customily_options gửi BE
}>()

const emit = defineEmits<{
  (e: 'update:personalization', optName: string, value: string): void
  (e: 'upload-image', event: Event, optName: string, callbacks: { startLoading: () => void, stopLoading: () => void, saveToHistory: (image: string, uploadUrl: string) => void }): void
  (e: 'remove-image', optName: string): void
  (e: 'choose-history-image', image: string, uploadUrl: string, optName: string): void
  (e: 'update:same-as-previous', value: boolean): void
}>()

const productStore = useProductStore()
const customizationStore = useCustomizationStore()
const loadingAI = ref(false)
const uploadProgress = ref(0)
const statusGenAIText = ref<'idle' | 'waiting'>('idle')

// Voice template (CMS output_type = voice): mp3 sinh từ BytePlus qua BE /style_transfer/voice
const statusGenVoice = ref<'idle' | 'waiting'>('idle')
const voiceUploading = ref(false)
const voiceError = ref('')
// Voice-only template: không có luồng ảnh, ẩn nút "Create Your Design"
const isVoice = computed(() => props.activeTemplate?.outputType === 'voice')
// Template có voice (voice-only hoặc template ảnh kèm khối voice): hiện nút voice độc lập với nút ảnh
const showVoice = computed(() => !!props.activeTemplate?.hasVoice)
// Mode fixed_text / both: khách tự gửi voice; fixed_voice: giọng cố định do CMS cấu hình
const needsVoiceInput = computed(() => showVoice.value && props.activeTemplate?.voiceMode !== 'fixed_voice')
const VOICE_MAX_FILE_SIZE = 10 * 1024 * 1024

// Bọc một nhóm ô thành thẻ riêng khi card = true; ngược lại chỉ là fragment (không thêm DOM).
const GroupWrapper = defineComponent({
  props: { card: { type: Boolean, default: false } },
  setup(groupProps, { slots }) {
    return () =>
      groupProps.card ? h('div', { class: 'layout-card' }, slots.default?.()) : slots.default?.()
  },
})

/**
 * Các nhóm hiển thị. Mỗi nhóm gồm các ô nhập (giữ index gốc trong optionsConfig) và, khi có voice,
 * một entry "action" (nút Create Your Voice + âm thanh).
 * - Không cấu hình "Voice fields": một nhóm duy nhất như cũ (action nằm cuối), không thêm DOM.
 * - Có "Voice fields": hai thẻ riêng. Thẻ voice gồm các ô đó + action, thẻ ảnh gồm các ô còn lại;
 *   thứ tự hai thẻ theo voiceFirst. Thứ tự ô trong mỗi thẻ giữ theo optionsConfig (sortOrder).
 */
const layoutGroups = computed(() => {
  const options: any[] = props.optionsConfig || []
  const fields = options.map((opt: any, index: number) => ({
    key: opt.name,
    opt,
    index,
    isAction: false,
  }))
  if (!showVoice.value) return [{ key: 'all', card: false, items: fields }]

  const action = { key: '__voice_action', opt: null as any, index: -1, isAction: true }
  const voiceNames: string[] = props.activeTemplate?.voiceFieldNames || []
  const voiceFields = fields.filter((entry) => voiceNames.includes(entry.opt.name))
  if (!voiceFields.length) return [{ key: 'all', card: false, items: [...fields, action] }]

  const imageFields = fields.filter((entry) => !voiceNames.includes(entry.opt.name))
  const imageGroup = { key: 'image', card: true, items: imageFields }
  const voiceGroup = { key: 'voice', card: true, items: [...voiceFields, action] }
  return props.activeTemplate?.voiceFirst ? [voiceGroup, imageGroup] : [imageGroup, voiceGroup]
})

const isSameAsPrevious = ref(true)

function selectSameAsPrevious() {
  if (!isSameAsPrevious.value) {
    isSameAsPrevious.value = true
    emit('update:same-as-previous', true)
  }
}

function selectDifferentDesign() {
  if (isSameAsPrevious.value) {
    isSameAsPrevious.value = false
    emit('update:same-as-previous', false)
  }
}

// Danh sách các tùy chọn thuộc loại tải ảnh lên (MediaUploader) của product (chung cho mọi pack)
const mediaUploaderOptions = computed(() => {
  return (props.optionsConfig || []).filter((opt: any) => opt.type === 'MediaUploader')
})

// Vị trí field "Any Change..." trong optionsConfig (-1 nếu không có field này)
const anyChangeFieldIndex = computed(() => {
  return (props.optionsConfig || []).findIndex((opt: any) =>
    (opt.label || '').toLowerCase().includes('any change')
  )
})
let uploadProgressInterval: any = null

let fabricCanvas: fabric.Canvas | null = null

const aiStyleTransferRecently = ref<
  Array<{
    image: string
    createdAt: number
    imageUploadUrl: string
    option_sort_id: string
  }>
>([])

const aiStyleTransferRecentlyIndexSelected = ref(-1)
const AI_STYLE_TRANSFER_RECENTLY_CACHE_PREFIX = 'AI-style-transfer-recently'

const filteredHistory = computed(() => {
  if (!aiStyleTransferRecently.value) return []
  const sortId = props.activeTemplateId || ''
  return aiStyleTransferRecently.value.filter(
    (item) => item.option_sort_id === String(sortId)
  )
})

function cleanupCanvas() {
  try {
    if (fabricCanvas) {
      try {
        fabricCanvas.dispose()
      } catch (e) {
        console.error('Error disposing canvas:', e)
      }
      fabricCanvas = null
    }
    const existingWrapper = document.querySelector(
      `.${CUSTOMILY_CANVAS_WRAPPER_CLASS}`
    )
    if (existingWrapper) {
      existingWrapper.remove()
    }
    productStore.productPreviewImage = null
    productStore.hasPreview = productStore.packPreviewImages.some((img) => !!img)
    const productPreviewSlide = document.getElementById(
      PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ID
    )
    if (productPreviewSlide) {
      productPreviewSlide.classList.remove(
        PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ACTIVE_CLASS
      )
    }
  } catch (error) {}
}

function setupCanvas() {
  try {
    cleanupCanvas()

    const productPreviewSlide = document.getElementById(
      PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ID
    )
    if (!productPreviewSlide || !props.activeTemplate) {
      return
    }

    const canvasWidth = props.activeTemplate.canvasSettings.width || 1000
    const canvasHeight = props.activeTemplate.canvasSettings.height || 1000

    const wrapper = document.createElement('div')
    wrapper.className = CUSTOMILY_CANVAS_WRAPPER_CLASS
    wrapper.style.width = '100%'
    wrapper.style.height = 'auto'
    wrapper.style.position = 'relative'

    const canvasEl = document.createElement('canvas')
    canvasEl.id = CUSTOMILY_PREVIEW_CANVAS_ID
    canvasEl.width = canvasWidth
    canvasEl.height = canvasHeight

    wrapper.appendChild(canvasEl)
    productPreviewSlide.appendChild(wrapper)
    productPreviewSlide.classList.add(
      PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ACTIVE_CLASS
    )

    fabricCanvas = new fabric.Canvas(canvasEl, {
      width: canvasWidth,
      height: canvasHeight,
      selection: false,
      hoverCursor: 'default',
      containerClass: CUSTOMILY_CANVAS_CONTAINER_CLASS,
    })
  } catch (error) {}
}

function addOrUpdateTextLayer(
  id: string,
  text: string,
  options: TextLayerOptions
) {
  try {
    if (!fabricCanvas) return

    const existingText = fabricCanvas
      .getObjects()
      .find((obj: fabric.Object & { frameId?: string }) => obj.frameId === id)

    if (existingText && existingText.type === 'text') {
      const textObj = existingText as fabric.Text
      textObj.set({
        text,
        left: options.left,
        top: options.top,
        fontSize: options.fontSize ?? 36,
        fontFamily: options.fontFamily ?? 'Arial',
        fontWeight: options.fontWeight ?? 'bold',
        fill: options.fill ?? '#ffffff',
        originX: options.originX ?? 'center',
        originY: options.originY ?? 'top',
        textAlign: options.textAlign ?? 'center',
        angle: options.angle ?? 0,
        opacity: options.opacity ?? 1,
      })
    } else {
      if (existingText) {
        fabricCanvas.remove(existingText)
      }
      const textObj = new fabric.Text(text, {
        left: options.left,
        top: options.top,
        fontSize: options.fontSize ?? 36,
        fontFamily: options.fontFamily ?? 'Arial',
        fontWeight: options.fontWeight ?? 'bold',
        fill: options.fill ?? '#ffffff',
        originX: options.originX ?? 'center',
        originY: options.originY ?? 'top',
        textAlign: options.textAlign ?? 'center',
        angle: options.angle ?? 0,
        opacity: options.opacity ?? 1,
        selectable: false,
        evented: false,
      })
      ;(textObj as fabric.Text & { frameId?: string }).frameId = id
      fabricCanvas.add(textObj)
    }
    sortCanvasObjects()
  } catch (error) {}
}

function addOrUpdateImageLayer(
  id: string,
  imageUrl: string,
  options: ImageLayerOptions
) {
  try {
    if (!fabricCanvas) return

    const existingImg = fabricCanvas
      .getObjects()
      .find((obj: fabric.Object & { frameId?: string }) => obj.frameId === id)

    if (imageUrl) {
      fabric.Image.fromURL(
        imageUrl,
        (img: fabric.Image) => {
          if (existingImg) {
            fabricCanvas?.remove(existingImg)
          }
          img.set({
            left: options.left,
            top: options.top,
            scaleX: options.width / (img.width || 1),
            scaleY: options.height / (img.height || 1),
            angle: options.angle ?? 0,
            opacity: options.opacity ?? 1,
            selectable: false,
            evented: false,
          })
          ;(img as fabric.Image & { frameId?: string }).frameId = id
          fabricCanvas?.add(img)
          sortCanvasObjects()
        },
        { crossOrigin: 'anonymous' }
      )
    } else if (existingImg) {
      fabricCanvas.remove(existingImg)
      sortCanvasObjects()
    }
  } catch (error) {}
}

function sortCanvasObjects() {
  try {
    if (!fabricCanvas || !props.activeTemplate) return

    const layers = props.activeTemplate.layers || []
    const objects = fabricCanvas.getObjects()

    objects.sort((a: any, b: any) => {
      const aIndex = layers.findIndex((l: any) => l.id === a.frameId)
      const bIndex = layers.findIndex((l: any) => l.id === b.frameId)
      return bIndex - aIndex
    })

    objects.forEach((obj, idx) => {
      fabricCanvas!.moveTo(obj, idx)
    })

    fabricCanvas.renderAll()
    if (fabricCanvas) {
      const dataUrl = fabricCanvas.toDataURL({ format: 'png' })
      productStore.productPreviewImage = dataUrl
      productStore.packPreviewImages[props.packIdx - 1] = dataUrl
    }
  } catch (error) {}
}

function initTemplateLayers() {
  try {
    if (!fabricCanvas || !props.activeTemplate) return

    let bgImgUrl = props.activeTemplate.canvasSettings.backgroundImageUrl
    if (props.isStyleTransferMode && props.personalizationValues['imageOutputUrl']) {
      bgImgUrl = props.personalizationValues['imageOutputUrl']
    }

    if (bgImgUrl) {
      productStore.productPreviewImage = bgImgUrl
      productStore.packPreviewImages[props.packIdx - 1] = bgImgUrl
      fabric.Image.fromURL(
        bgImgUrl,
        (img: fabric.Image) => {
          if (!img) {
            try {
              Analytics.track('Canvas - render error', {
                packIdx: props.packIdx,
                bgImgUrl,
                error: 'bgImg load failed (img is null)',
              })
            } catch (e) {}
            return
          }
          img.set({
            originX: 'left',
            originY: 'top',
            scaleX: fabricCanvas!.width! / img.width!,
            scaleY: fabricCanvas!.height! / img.height!,
          })
          fabricCanvas!.setBackgroundImage(img, () => {
            fabricCanvas!.renderAll()
            if (fabricCanvas) {
              try {
              const dataUrl = fabricCanvas.toDataURL({ format: 'png' })
              productStore.productPreviewImage = dataUrl
              productStore.packPreviewImages[props.packIdx - 1] = dataUrl
              } catch (toDataUrlError: any) {
                try {
                  Analytics.track('Canvas - render error', {
                    packIdx: props.packIdx,
                    bgImgUrl,
                    error: toDataUrlError?.message || String(toDataUrlError),
                    isTainted: String(toDataUrlError?.message || '').includes('Tainted'),
                  })
                } catch (e) {}
              }
            }
          })
        },
        { crossOrigin: 'anonymous' }
      )
    } else {
      fabricCanvas.renderAll()
      if (fabricCanvas) {
        try {
        const dataUrl = fabricCanvas.toDataURL({ format: 'png' })
        productStore.productPreviewImage = dataUrl
        productStore.packPreviewImages[props.packIdx - 1] = dataUrl
        } catch (e) {}
      }
    }

    if (props.activeTemplate.layers) {
      props.activeTemplate.layers.forEach((layer: any) => {
        const optionKey = layer.linkedOptionId || ''
        if (layer.type === 'text') {
          const textVal = props.personalizationValues[optionKey] || ''
          addOrUpdateTextLayer(layer.id, textVal, {
            left: layer.left + layer.width / 2,
            top: layer.top,
            fontSize: (layer.style?.fontSize as number) ?? 36,
            fontFamily: (layer.style?.fontFamily as string) ?? 'Arial',
            fontWeight: (layer.style?.fontWeight as string) ?? 'bold',
            fill: (layer.style?.fill as string) ?? '#ffffff',
            originX: 'center',
            angle: layer.angle,
            opacity: layer.opacity,
          })
        } else if (layer.type === 'image') {
          let imgUrl = props.personalizationValues[optionKey] || ''
          if (!imgUrl && layer.imageOptions && layer.imageOptions.length > 0) {
            imgUrl = layer.imageOptions[0].src || ''
          }
          if (!imgUrl && layer.placeholderUrl) {
            imgUrl = layer.placeholderUrl || ''
          }
          addOrUpdateImageLayer(layer.id, imgUrl, {
            left: layer.left,
            top: layer.top,
            width: layer.width,
            height: layer.height,
            angle: layer.angle,
            opacity: layer.opacity,
          })
        }
      })
    }

    sortCanvasObjects()
    const hasCustomizedValues = Object.values(props.personalizationValues).some(
      (val) => !!val
    )
    productStore.hasPreview = productStore.packPreviewImages.some((img, idx) => {
      if (idx === props.packIdx - 1) {
        return hasCustomizedValues
      }
      return !!img
    })
    EventBus.trigger(EVENT_PRODUCT_UPDATE_MAIN_SLIDER)
  } catch (error) {}
}

async function getAiStyleTransferRecently() {
  try {
    const productHandle = productStore.product?.handle
    if (!productHandle) return []
    const cacheKey = `${AI_STYLE_TRANSFER_RECENTLY_CACHE_PREFIX}_${productHandle}_pack_${props.packIdx}`
    const cacheResponse = await CacheService.instance?.get(cacheKey)
    const aiStyleTransferRecentlyFromCache = getJSONfromCacheResponse(cacheResponse)
    if (aiStyleTransferRecentlyFromCache) {
      const now = Date.now()
      const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
      aiStyleTransferRecently.value = aiStyleTransferRecentlyFromCache.filter(
        (item: { createdAt: number }) => {
          const itemTime = new Date(item.createdAt).getTime()
          return itemTime >= sevenDaysAgo && itemTime <= now
        }
      )
      return aiStyleTransferRecently.value
    }
    return []
  } catch (error) {
    return []
  }
}

async function setAiStyleTransferRecently(image: string, imageUploadUrl: string) {
  try {
    const productHandle = productStore.product?.handle
    if (!productHandle) return
    const cacheKey = `${AI_STYLE_TRANSFER_RECENTLY_CACHE_PREFIX}_${productHandle}_pack_${props.packIdx}`
    const cacheResponse = await CacheService.instance?.get(cacheKey)
    const aiStyleTransferRecentlyFromCache = getJSONfromCacheResponse(cacheResponse)
    await CacheService.instance?.delete(cacheKey)

    if (aiStyleTransferRecentlyFromCache?.length) {
      const now = Date.now()
      const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
      aiStyleTransferRecently.value = aiStyleTransferRecentlyFromCache.filter(
        (item: { createdAt: number }) => {
          const itemTime = new Date(item.createdAt).getTime()
          return itemTime >= sevenDaysAgo && itemTime <= now
        }
      )
    }

    aiStyleTransferRecently.value = [
      {
        image,
        createdAt: Date.now(),
        imageUploadUrl,
        option_sort_id: props.activeTemplateId || '',
      },
      ...(aiStyleTransferRecently.value || []),
    ]
    CacheService.instance?.set(
      cacheKey,
      cloneDeep(aiStyleTransferRecently.value),
      SEVEN_DAYS_IN_MILLISECONDS
    )
  } catch (error) {}
}

function startUploadProgress() {
  uploadProgress.value = 0
  if (uploadProgressInterval) clearInterval(uploadProgressInterval)
  uploadProgressInterval = setInterval(() => {
    if (uploadProgress.value < 98) {
      const step = 1.63 + (Math.random() * 0.4 - 0.2)
      uploadProgress.value = Math.min(98, Math.round(uploadProgress.value + step))
    } else if (uploadProgress.value < 99) {
      uploadProgress.value = 99
    }
  }, 1000)
}

function finishUploadProgress() {
  if (uploadProgressInterval) {
    clearInterval(uploadProgressInterval)
    uploadProgressInterval = null
  }
  uploadProgress.value = 100
}

function handleUploadImage(event: Event, optName: string) {
  emit('upload-image', event, optName, {
    startLoading: () => {
      loadingAI.value = true
      startUploadProgress()
    },
    stopLoading: () => {
      finishUploadProgress()
      loadingAI.value = false
    },
    saveToHistory: (image: string, uploadUrl: string) => {
      setAiStyleTransferRecently(image, uploadUrl)
      aiStyleTransferRecentlyIndexSelected.value = 0
    }
  })
}

/**
 * Kiểm tra option value hiện có đang được chọn trong Dropdown hay không (hỗ trợ cả single và multiple).
 */
function isDropdownOptionSelected(opt: { name: string; multiple?: boolean }, value: string): boolean {
  const current = props.personalizationValues[opt.name] || ''
  if (!opt.multiple) return current === value
  return current.split(',').filter(Boolean).includes(value)
}

/**
 * Xử lý khi người dùng chọn Dropdown (single hoặc multiple), gộp giá trị multiple thành chuỗi phân cách bằng dấu phẩy.
 */
function onDropdownChange(e: Event, opt: { name: string; multiple?: boolean }) {
  const select = e.target as HTMLSelectElement
  if (opt.multiple) {
    const values = Array.from(select.selectedOptions).map((o) => o.value)
    emit('update:personalization', opt.name, values.join(','))
  } else {
    emit('update:personalization', opt.name, select.value)
  }
}

function chooseHistoryImage(index: number, optName: string) {
  aiStyleTransferRecentlyIndexSelected.value = index
  const item = filteredHistory.value[index]
  if (!item) return
  emit('choose-history-image', item.image, item.imageUploadUrl, optName)
}

/**
 * Case text-only: pack này không có field MediaUploader nào (không có ảnh khách upload).
 * Khách bấm "Create Your Design" để trigger AI generate thẳng từ prompt + text đã điền cho riêng pack này.
 */
async function handleGenImageFromOnlyText() {
  statusGenAIText.value = 'waiting'
  // Tái dùng đúng cơ chế đếm % giả lập của luồng upload ảnh (startUploadProgress/
  // finishUploadProgress) — 2 luồng không bao giờ hiện cùng lúc (mediaUploaderOptions
  // rỗng mới hiện nút text-only này) nên dùng chung uploadProgress an toàn.
  startUploadProgress()
  try {
    const promptCustom: Record<string, string> = {}
    props.optionsConfig.forEach((opt: any) => {
      if (opt.customPromptKey) {
        promptCustom[opt.customPromptKey] = props.personalizationValues[opt.name] || ''
      }
    })

    const productVariants: Record<string, string> = {}
    productStore.selectedVariant?.selectedOptions?.forEach((opt: any) => {
      productVariants[opt.name] = opt.value
    })
    // Gộp pill (Multi Prompt) đang chọn — giống hệt triggerAiStyleTransfer bên cha,
    // thiếu dòng này thì BE không re-check được condition khi có entry_id.
    Object.assign(productVariants, props.pillCustomilyOptions || {})

    const payload = {
      templateId: '9999',
      id: props.activeTemplateId || '',
      // Entry Multi Variant/Multi Prompt đang chọn — thiếu field này BE luôn fallback
      // về prompt mặc định của template, bỏ qua mọi pill/variant đã set (bug đã gặp).
      entry_id: props.entryId || '',
      custom_name: '',
      prompt_custom: Object.keys(promptCustom).length ? JSON.stringify(promptCustom) : '',
      customily_options: Object.keys(productVariants).length
        ? JSON.stringify(productVariants)
        : '',
    }

    const finalImageUrl = await customizationStore.transferTextToImage(payload)
    if (!finalImageUrl) {
      throw new Error('Failed to generate image')
    }

    // Ảnh AI xử lý bất đồng bộ ở BE — link trả về có thể chưa tồn tại file thật ngay,
    // phải poll tới khi load được (giống hệt triggerAiStyleTransfer bên cha).
    const isReady = await waitForImageReady(finalImageUrl)
    if (!isReady) {
      throw new Error('Image ready check timed out')
    }

    // Ghi qua emit để cha đồng bộ vào đúng packVals của pack này (giống hệt cơ chế
    // handlePackOptionInput cho các field khác) — canvas thật do chính component này
    // sở hữu nên watch(personalizationValues) bên dưới sẽ tự vẽ lại background.
    emit('update:personalization', 'imageOutputUrl', finalImageUrl)
    setAiStyleTransferRecently(finalImageUrl, finalImageUrl)
    aiStyleTransferRecentlyIndexSelected.value = 0
    EventBus.trigger(EVENT_PRODUCT_SHOW_PREVIEW)
  } catch (error) {
    console.error(error)
  } finally {
    finishUploadProgress()
    statusGenAIText.value = 'idle'
  }
}

/**
 * Polling liên tục kiểm tra xem ảnh đã được AI xử lý xong và tải thành công lên Cloud chưa.
 */
async function waitForImageReady(url: string, interval = 10000, maxAttempts = 60) {
  try {
    for (let i = 1; i <= maxAttempts; i++) {
      const isReady = await new Promise<boolean>((resolve) => {
        const img = new Image()
        img.onload = () => resolve(true)
        img.onerror = () => resolve(false)
        img.src = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now()
      })
      if (isReady) return true
      if (i < maxAttempts) {
        await new Promise((r) => setTimeout(r, interval))
      }
    }
    return false
  } catch (error) {
    return false
  }
}

async function handleVoiceFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  voiceError.value = ''
  if (file.size > VOICE_MAX_FILE_SIZE) {
    voiceError.value = 'Your voice file is too large (max 10 MB).'
    input.value = ''
    return
  }
  voiceUploading.value = true
  try {
    const provider = await Provider.getInstance()
    const url = await provider.uploadAudio(file)
    if (!url) throw new Error('Upload failed')
    emit('update:personalization', 'voiceInputUrl', url)
  } catch (error) {
    console.error(error)
    voiceError.value = 'Could not upload your voice. Please try an MP3 or WAV file.'
  } finally {
    voiceUploading.value = false
    input.value = ''
  }
}

/**
 * Voice template: khách bấm "Create Your Voice" để BE sinh mp3 từ giọng cố định (hoặc giọng khách upload) và text.
 * Text lấy từ các field có customPromptKey (cùng cơ chế prompt_custom của luồng ảnh).
 */
async function handleGenVoice() {
  voiceError.value = ''
  const voiceUrl = props.personalizationValues['voiceInputUrl'] || ''
  if (needsVoiceInput.value && !voiceUrl) {
    voiceError.value = 'Please upload your voice first.'
    return
  }
  const promptCustom: Record<string, string> = {}
  let missingRequired = false
  props.optionsConfig.forEach((opt: any) => {
    if (!opt.customPromptKey) return
    const val = (props.personalizationValues[opt.name] || '').trim()
    if (opt.required && !val) missingRequired = true
    promptCustom[opt.customPromptKey] = val
  })
  if (missingRequired) {
    voiceError.value = 'Please fill in all required fields.'
    return
  }

  statusGenVoice.value = 'waiting'
  try {
    const url = await customizationStore.transferVoice({
      id: props.activeTemplateId || '',
      voiceKey: props.activeTemplate?.voiceKey,
      promptCustom: Object.keys(promptCustom).length ? JSON.stringify(promptCustom) : '',
      voiceUrl,
    })
    if (!url) throw new Error('Failed to create voice')

    // mp3 được BE sinh bất đồng bộ — link trả về có thể chưa tồn tại ngay, poll tới khi phát được.
    const isReady = await waitForAudioReady(url)
    if (!isReady) throw new Error('Voice ready check timed out')

    emit('update:personalization', 'voiceOutputUrl', url)
  } catch (error) {
    console.error(error)
    voiceError.value = 'We could not create your voice. Please check your input and try again.'
  } finally {
    statusGenVoice.value = 'idle'
  }
}

async function waitForAudioReady(url: string, interval = 3000, maxAttempts = 40) {
  for (let i = 1; i <= maxAttempts; i++) {
    const isReady = await new Promise<boolean>((resolve) => {
      const audio = new Audio()
      const timer = setTimeout(() => resolve(false), 8000)
      audio.preload = 'metadata'
      audio.onloadedmetadata = () => {
        clearTimeout(timer)
        resolve(true)
      }
      audio.onerror = () => {
        clearTimeout(timer)
        resolve(false)
      }
      audio.src = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now()
    })
    if (isReady) return true
    if (i < maxAttempts) {
      await new Promise((r) => setTimeout(r, interval))
    }
  }
  return false
}

// Theo dõi khi item này được kích hoạt active
watch(
  () => props.activePackIdx,
  async (newActiveIdx) => {
    if (newActiveIdx === props.packIdx) {
      await nextTick()
      setupCanvas()
      initTemplateLayers()
    } else {
      cleanupCanvas()
    }
  },
  { immediate: true }
)

// Theo dõi sự thay đổi của options để vẽ đè lên canvas nếu item này đang active
watch(
  () => props.personalizationValues,
  () => {
    if (props.activePackIdx === props.packIdx && fabricCanvas) {
      initTemplateLayers()
    }
  },
  { deep: true }
)

onMounted(() => {
  if (props.isStyleTransferMode) {
    getAiStyleTransferRecently()
  }
})

onUnmounted(() => {
  cleanupCanvas()
  if (uploadProgressInterval) clearInterval(uploadProgressInterval)
})
</script>

<style scoped lang="scss">
/* Không dùng class "customily-preview" (có border/background box từ theme) vì nút này
   giờ nằm bên trong box field của pack, dùng lại sẽ tạo box lồng box nhìn rối. */
.gen-only-text-action {
  margin-top: 12px;
  margin-bottom: 12px;
}

/* Thẻ riêng cho khối ảnh và khối voice (CMS: Voice fields), nằm trong khung personalization */
.layout-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.layout-card > :last-child {
  margin-bottom: 0;
}

.pack-copy-option-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.pack-copy-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    border-color: #d1d5db;
  }

  &.active {
    border-color: var(--color-primary);
    background-color: var(--background-color-primary, #eef8f3);

    .card-radio .radio-custom {
      border-color: var(--color-primary);
      background-color: #ffffff;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: var(--color-primary);
      }
    }
  }

  .card-radio {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    input[type='radio'] {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
      margin: 0;
    }

    .radio-custom {
      position: relative;
      display: inline-block;
      width: 18px;
      height: 18px;
      border: 2px solid #9ca3af;
      border-radius: 50%;
      box-sizing: border-box;
      transition: all 0.2s ease;
    }
  }

  .card-content {
    display: flex;
    flex-direction: column;
    text-align: left;

    .card-title {
      font-weight: 600;
      font-size: 14px;
      line-height: 1.4;
      color: #374151;
    }

    .card-subtitle {
      font-size: 12px;
      color: #6b7280;
      margin-top: 2px;
    }
  }
}
</style>
